import type { SeasonOpening } from "@/types";

/**
 * Opening the pool, with the log keeping score.
 *
 * A `season_open` supersedes every parameter and ends every carry (ADR 0010),
 * which is right — the water is new — and which leaves the dashboard at its
 * emptiest on the one day of the year with the most to do. This card is what
 * stands in that gap: the order to establish the water in, and how far through
 * it you already are.
 *
 * **Completed steps stay on screen, greyed and ticked.** Filtering them out
 * would make the card shorter and would turn progress into a nag that never
 * visibly shrinks. Nothing is stored to make this work — every tick is
 * re-derived from readings, doses and events on each load, so there is no
 * checklist that can drift out of step with the log, and nothing the app
 * recorded that the owner did not (non-negotiable #7).
 *
 * The card removes itself when the last step goes green. There is no "finish"
 * button because there is no state to finish.
 */
export default function SeasonOpeningCard({ opening }: { opening: SeasonOpening }) {
  return (
    <section className="card p-4">
      <h2 className="unit">Opening the pool</h2>
      <p className="mt-1 text-sm text-(--color-ink-muted)">{opening.note}</p>

      <ol className="mt-4 space-y-3">
        {opening.steps.map((step, index) => (
          <li key={step.key} className="flex gap-3">
            {/* A number while outstanding, a tick once done. The marker carries
                the state so the step can be read at a glance without relying on
                the text colour alone — which is the same reason the parameter
                rows pair a tint with words. */}
            <span
              aria-hidden
              className="figure mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs"
              style={{
                background: step.done ? "var(--color-status-ok)" : "var(--color-border-subtle)",
                color: step.done ? "white" : "var(--color-ink-muted)",
              }}
            >
              {step.done ? "✓" : index + 1}
            </span>
            <div className="min-w-0">
              <p
                className="text-sm font-medium"
                style={step.done ? { color: "var(--color-ink-faint)" } : undefined}
              >
                {step.title}
                <span className="sr-only">{step.done ? " — done" : " — still to do"}</span>
              </p>
              {/* The reasoning is only worth screen space while the step is
                  outstanding. Once it is done the title alone is the record. */}
              {!step.done && (
                <p className="mt-0.5 text-sm text-(--color-ink-muted)">{step.detail}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
